// English is the source of truth: its keys define what every other language must provide.
// Adding a key here makes the build fail until each language file supplies it.
export const strings = {
  'menu.home': 'Home',
  'menu.notes': 'Notes',
  'menu.reminders': 'Reminders',
  'menu.passwords': 'Passwords',
  'menu.cards': 'Cards',
  'menu.photoIds': 'Photo IDs',
  'menu.collections': 'Collections',
  'menu.conflicts': 'Conflicts ({count})',
  'menu.settings': 'Settings',
  'menu.lock': 'Lock wallet',
  'menu.language': 'Language',

  'type.note': 'Note',
  'type.note.plural': 'Notes',
  'type.password': 'Password',
  'type.password.plural': 'Passwords',
  'type.card': 'Card',
  'type.card.plural': 'Cards',
  'type.photoId': 'Photo ID',
  'type.photoId.plural': 'Photo IDs',

  'section.upcoming': 'Upcoming reminders',
  'section.pinned': 'Pinned',
  'section.recent': 'Recent',
  'section.searchResults': 'Search results',
  'section.conflicts': 'Conflicts',

  'action.addNew': '+ Add new',
  'action.back': 'Back',
  'search.placeholder': 'Search or add new',

  'gate.title': 'Everything important',
  'gate.copy': 'Your wallet is encrypted on this device before anything is synchronized.',
  'gate.create': 'Create a new wallet',
  'gate.open': 'Open my existing wallet',
  'gate.recover': 'Recover wallet access',

  'empty.addFirst': 'Add your first item.',
  'empty.nothingHere': 'Nothing here yet.',
  'empty.noResults': 'No saved items match that search.',

  // Shared actions.
  'action.cancel': 'Cancel',
  'action.save': 'Save',
  'action.saving': 'Saving…',
  'action.close': 'Close',
  'action.edit': 'Edit',
  'action.delete': 'Delete',
  'action.remove': 'Remove',
  'action.change': 'Change',
  'action.clear': 'Clear',
  'action.add': 'Add',
  'action.show': 'Show',
  'action.hide': 'Hide',
  'action.more': 'More',

  // Field labels shown on screen. The names stored inside a card stay English, so an
  // existing wallet keeps working; only what the reader sees is translated.
  'field.title': 'Title',
  'field.body': 'Body',
  'field.username': 'Username',
  'field.password': 'Password',
  'field.website': 'Website',
  'field.notes': 'Notes',
  'field.cardNumber': 'Card number',
  'field.documentNumber': 'Document number',
  'field.expiry': 'Expiry',
  'field.securityCode': 'Security code',
  'field.cvv': 'CVV / CVC',
  'field.pin': 'PIN',
  'field.documentType': 'Document type',
  'field.reminder': 'Reminder',
  'field.collections': 'Collections',
  'field.idPhotos': 'ID photos',
  'field.attachments': 'Attachments',

  'editor.newItem': 'New {type}',
  'editor.editItem': 'Edit {type}',
  'editor.titlePlaceholder': '{type} title',
  'editor.selectDocumentType': 'Select document type',
  'editor.noneSelected': 'None selected',
  'editor.optional': 'Optional',
  'editor.optionalFields': 'Optional fields',
  'editor.reminderDate': 'Reminder date',
  'editor.optionalTime': 'Optional time',
  'editor.shortcutHint': 'Ctrl+Enter saves. Esc cancels.',
  'editor.addTitle': 'Add a title.',
  'editor.saveFailed': 'Could not save this item.',
  'editor.photoFailed': 'Could not prepare that photo.',
  'editor.attachmentFailed': 'Could not prepare that attachment.',
  'editor.photoNote': 'Photos up to {mb} MB are encrypted and saved on this device, then synced automatically if wallet sync is on.',
  'editor.attachmentNote': 'Files are encrypted exactly as selected and saved on this device. Safe Wallet blocks common executable, script, active web, and macro-enabled file types, but it does not scan files for malware.',
  'editor.attachmentButton': 'Attachment (max {mb} MB)',
  'editor.addingAttachment': 'Adding attachment…',
  'editor.preparing': 'Preparing…',
  'editor.takePhoto': 'Take photo',
  'editor.choosePhoto': 'Choose photo',
  'editor.front': 'Front',
  'editor.back': 'Back',
  'editor.tapAddFront': 'Tap to add front photo',
  'editor.tapAddBack': 'Tap to add back photo',
  'editor.photoNotOnDevice': 'Encrypted photo is not stored on this device.',
  'editor.pinItem': 'Pin item',
  'editor.pinned': 'Pinned',
  'editor.deleteItem': 'Delete item',

  'viewer.noBody': 'No body text.',
  'viewer.notSet': 'Not set',
  'viewer.escCloses': 'Esc closes this view.',

  'attach.title': 'Attachment',
  'attach.notOnDevice': 'Encrypted attachment is not stored on this device yet.',
  'attach.photoNotOnDevice': 'Encrypted photo is not stored on this device yet.',
  'attach.openingPhoto': 'Opening encrypted photo…',
  'attach.opening': 'Opening…',
  'attach.notStored': 'Not stored on this device',
  'attach.couldNotOpen': 'Could not open this attachment.',
  'attach.previewUnavailable': 'Preview is not available for this file type. Download it and open it with an app you trust.',
  'attach.previewLimited': 'Preview limited to the first 1 MB.',
  'attach.openPdf': 'Open PDF',
  'attach.download': 'Download',

  'expiry.label': 'Expiry (MM/YY)',
  'expiry.month': 'Expiry month',
  'expiry.chooseMonthYear': 'Choose expiry month and year',
  'expiry.chooseDate': 'Choose expiry date',
  'expiry.previousYear': 'Previous year',
  'expiry.nextYear': 'Next year',

  'collections.createFirst': 'Create Collections from the main menu first.',
  'chooser.addNew': 'Add new',

  // Settings screen.
  'settings.title': 'Settings',
  'settings.account': 'Account',
  'settings.security': 'Security',
  'settings.recovery': 'Recovery',
  'settings.thisDevice': 'This device',
  'settings.appearance': 'Appearance',
  'settings.searchPrivacy': 'Search privacy',
  'settings.dangerZone': 'Danger zone',
  'settings.notSignedIn': 'Not signed in',
  'settings.pinOnly': 'PIN only',
  'settings.pinAndFingerprint': 'PIN + fingerprint',
  'settings.changePinRow': 'Change the app PIN for this device. Your current PIN is required.',
  'settings.lockNow': 'Lock now',
  'settings.chooseAnotherWallet': 'Choose another wallet',
  'settings.useAnotherAccount': 'Use another account',
  'settings.categoryLabels': 'Category labels',
  'settings.rowSpacing': 'Row spacing',
  'settings.textSize': 'Text size',
  'settings.autoLock': 'Auto-lock',
  'settings.optionName': 'Name',
  'settings.optionIcon': 'Icon',
  'settings.optionBoth': 'Both',
  'settings.optionLarge': 'Large',
  'settings.optionMedium': 'Medium',
  'settings.optionCompact': 'Compact',
  'settings.optionSmall': 'Small',
  'settings.searchPrivacyNote': 'The decrypted search index exists only on this unlocked device and is erased when the wallet locks. Passwords, PINs, card and account numbers, security codes, ID numbers, recovery codes and similar secrets are never indexed.',
  'settings.exampleProjection': 'Example for the first item',
  'settings.noItemsYet': 'No items yet.',
  'settings.removeWalletNote': 'Removing this wallet from this browser does not delete its encrypted backup in the account.',
  'settings.removeWallet': 'Remove this local wallet',

  // Change PIN.
  'pin.changeTitle': 'Change app PIN',
  'pin.change': 'Change PIN',
  'pin.changing': 'Changing…',
  'pin.current': 'Current PIN',
  'pin.new': 'New PIN',
  'pin.repeat': 'Repeat new PIN',
  'pin.accountPassword': 'Account password',
  'pin.accountPasswordHint': 'Needed to update sign-in on your other devices.',
  'pin.noteSynced': 'Your saved items don’t need to be re-encrypted. Because this wallet syncs, your account password is needed so the new PIN also works when you sign in on a new device.',
  'pin.noteLocal': 'Your current PIN unlocks this wallet on this device. Your saved items don’t need to be re-encrypted.',
  'pin.errCurrent': 'Enter your current 6 to 12 digit app PIN.',
  'pin.errNew': 'Use a 6 to 12 digit new app PIN.',
  'pin.errMismatch': 'The two new PIN entries do not match.',
  'pin.errSame': 'Choose a new PIN that is different from the current PIN.',
  'pin.errPassword': 'Enter your account password.',
  'pin.errFailed': 'Could not change the app PIN.',

  // Fingerprint unlock.
  'fingerprint.title': 'Fingerprint unlock',
  'fingerprint.settingUp': 'Setting up…',
  'fingerprint.unavailable': 'This device can’t do that.',
  'fingerprint.errOn': 'Could not turn this on.',
  'fingerprint.errOff': 'Could not turn this off.',

  // Sync status.
  'sync.off': 'Off — only on this device',
  'sync.notConfigured': 'Off — sync not configured',
  'sync.connecting': 'Connecting…',
  'sync.uploading': 'On — syncing encrypted backup…',
  'sync.waiting': 'On — waiting to upload encrypted changes',
  'sync.connected': 'On — encrypted backup connected',
  'sync.offline': 'Offline — encrypted backup paused',

  // Account panel.
  'account.signedIn': 'Signed in',
  'account.signedInAs': 'Signed in as',
  'account.anotherAccount': 'Another account',
  'account.currentAccount': 'Current account',
  'account.wallet': 'Wallet',
  'account.sync': 'Sync',
  'account.device': 'Device',
  'account.signOut': 'Sign out',
  'account.signOutOf': 'Sign out of {account}',
  'account.email': 'Email',
  'account.pinLabel': 'Safe Wallet PIN',
  'account.pinHint': 'Your PIN for this wallet. If this account already has a wallet, use that wallet’s PIN.',
  'account.pinPlaceholder': '6–12 digits',
  'account.signInTab': 'Sign in',
  'account.createTab': 'Create account',
  'account.forgotPassword': 'Forgot account password?',
  'account.signInSync': 'Sign in & sync',
  'account.createSync': 'Create account & sync',
  'account.signInSyncTitle': 'Sign in & Sync',
  'account.differentSession': 'This browser has an account session, but this local wallet is not connected to that account. Open Sign in & Sync below to connect the account that owns this wallet, or sign out first for a different account.',
  'account.notConfigured': 'Sync is not configured for this deployment.',
  'account.confirmEmail': 'Check your email to confirm the account, then return and sign in. This local wallet is not connected yet.',
  'account.errPin': 'Enter the 6 to 12 digit Safe Wallet PIN for this wallet.',
  'account.errSession': 'The account session could not be established.',
  'account.connected': 'Wallet connected. Sync is starting.',
  'account.connectedNoSync': 'Account connected. Sync is not available in this environment.',
  'account.errConnect': 'Could not connect this account.',
  'account.resetSent': 'Check your email for a password-reset link.',
  'account.errReset': 'Could not send the reset email.',
  'account.signedOut': 'Signed out of the account. The encrypted wallet remains available on this device.',

  // Approving another device by fingerprint comparison (advanced).
  'pairing.advanced': 'Advanced: approve another device',
  'pairing.advancedNote': 'Use this only when the normal password + PIN sign-in is not available. Compare the fingerprint on both devices before approving.',
  'pairing.none': 'No pending devices.',
  'pairing.invalidKey': 'Invalid pairing key',
  'pairing.calculating': 'Calculating fingerprint…',
  'pairing.review': 'Review',
  'pairing.reviewTitle': 'Review new device',
  'pairing.fingerprint': 'Verification fingerprint',
  'pairing.compare': 'Compare every group with the fingerprint shown on the new device.',
  'pairing.doNotApprove': 'Do not approve a mismatch.',
  'pairing.mismatchNote': 'It may mean the pairing key was changed and the vault key could be sent to the wrong device.',
  'pairing.confirm': 'I compared both fingerprints and they match exactly.',
  'pairing.approve': 'Approve device',
  'pairing.approving': 'Approving…',
  'pairing.approved': 'Approved {device}.',
  'pairing.errInvalid': 'This approval request contains an invalid pairing key. Decline it and start a new request.',
  'pairing.errChanged': 'The pairing key changed while it was being reviewed. Cancel this request.',
  'pairing.errApprove': 'Could not approve that device.',

  // Wallet descriptions shown before unlock.
  'wallet.localOnly': 'Local-only wallet',
  'wallet.lastUsedRecently': 'Last used recently',
  'wallet.notConnected': 'Not connected to an account',
  'wallet.availableOnDevice': 'Wallet available on this device',
  'wallet.onlyThisDevice': 'Only on this device — sync is off',

  'fingerprint.confirmBackup': 'Turn on fingerprint unlock?\n\nAfter this, opening this wallet on this device needs your fingerprint as well as your PIN. If the fingerprint ever stops working, get back in with your backup login or a trusted contact.',
  'fingerprint.confirmNoBackup': 'Turn on fingerprint unlock?\n\nAfter this, opening this wallet on this device needs your fingerprint as well as your PIN. This wallet has no backup yet, so if the fingerprint ever stops working it cannot be opened again. Turning on sync first is strongly recommended.',

  'gate.cloudNotConfigured': 'Cloud setup is not configured yet. You can create a local wallet now and connect it later from Settings.',

  // Creating a wallet.
  'create.title': 'Create app PIN',
  'create.intro': 'The PIN unlocks this device. Your account password will be separate when you turn on synchronisation.',
  'create.walletName': 'Wallet name',
  'create.walletNameHint': 'Shown on this browser before PIN entry.',
  'create.walletNamePlaceholder': 'Alex, Sam, Family safe…',
  'create.deviceName': 'Device name',
  'create.appPin': 'App PIN',
  'create.repeatPin': 'Repeat PIN',
  'create.creating': 'Creating…',
  'create.createWallet': 'Create wallet',
  'create.errPin': 'Use a 6 to 12 digit app PIN.',
  'create.errMismatch': 'The two PIN entries do not match.',
  'create.errFailed': 'Could not create this wallet.',
  'create.errHello': 'Could not turn on fingerprint unlock.',
  'create.fingerprintTitle': 'Add fingerprint or face?',
  'create.fingerprintCopy': 'A second lock, so a stolen copy of your wallet can’t be broken into. You still enter your PIN.',
  'create.turnOn': 'Turn on',
  'create.notNow': 'Not now',

  // Unlocking.
  'unlock.title': 'Unlock Safe Wallet',
  'unlock.unlock': 'Unlock',
  'unlock.opening': 'Opening…',
  'unlock.withFingerprint': 'Unlock with fingerprint',
  'unlock.wait': 'Wait {seconds}s',
  'unlock.tooManyAttempts': 'Too many attempts. Try again in {seconds}s.',
  'unlock.tooMany': 'Too many incorrect PINs',
  'unlock.tooManyBackup': 'Take a moment. You can keep trying below, or recover your access with your backup login or a trusted contact.',
  'unlock.tooManyLocal': 'Take a moment and try again carefully. This wallet is only on this device, so there is no backup to fall back on.',
  'unlock.forgotPin': 'Forgot Safe Wallet PIN?',
  'unlock.errFailed': 'Could not open this wallet.',

  // Choosing between wallets on one browser.
  'chooser.chooseWallet': 'Choose a wallet',
  'chooser.eachHasPin': 'Each saved wallet has its own PIN on this browser.',
  'chooser.current': 'Current',
  'chooser.addAnother': 'Add another account',

  // Opening a wallet that already exists.
  'existing.title': 'Existing wallet',
  'existing.heading': 'Open my wallet',
  'existing.intro': 'Use your account password and Safe Wallet PIN to open this wallet on this device.',
  'existing.openWallet': 'Open wallet',
  'existing.forgotPin': 'Forgot your Safe Wallet PIN?',
  'existing.advanced': 'Advanced: approve using another device',
  'existing.advancedNote': 'Use this only for older Beta wallets that have not yet been upgraded for password + PIN sign-in.',
  'existing.requesting': 'Requesting…',
  'existing.requestApproval': 'Request device approval',
  'existing.approvalTitle': 'Device approval',
  'existing.unavailable': 'Unavailable',
  'existing.calculating': 'Calculating…',
  'existing.mismatchNote': 'A mismatch means the pairing key may have been changed in transit. Cancel the request and do not approve it.',
  'existing.cancelRequest': 'Cancel request',
  'existing.approved': 'Device approved',
  'existing.setPin': 'Set an app PIN',
  'existing.setPinNote': 'This PIN protects the approved wallet key on this device. You may use the same Safe Wallet PIN as on your other personal devices.',
  'existing.finishing': 'Finishing…',
  'existing.errPin': 'Use a 6 to 12 digit Safe Wallet PIN.',
  'existing.errNoWallet': 'This account does not have a Safe Wallet yet. Create a new wallet instead.',
  'existing.errUpgrade': 'One-time upgrade needed. Open this wallet once on a device where it already works, turn on sync there, then try again.',
  'existing.errRequest': 'Could not request device approval.',
  'existing.errCancel': 'Could not cancel the approval request; it will expire automatically.',
  'existing.errFinish': 'Could not finish device approval.',
  'existing.errPairingInvalid': 'The local pairing key is invalid. Cancel and start a new request.',
  'existing.errCancelled': 'That approval request was cancelled. Start a new request.',
  'existing.errExpired': 'That approval request expired. Start a new request.',

  // Header status and app-level state.
  'status.onDeviceOnly': 'On this device only',
  'status.syncing': 'Syncing…',
  'status.synced': 'Synced',
  'status.offline': 'Offline',
  'status.saving': 'Saving…',
  'status.savingLocal': 'Saving on this device…',
  'status.openingDatabase': 'Opening local database…',
  'status.couldNotStart': 'Could not start',
  'status.errInit': 'Could not initialize Safe Wallet.',
  'status.errDatabase': 'Local database is not ready.',
  'menu.closeMenu': 'Close menu',
  'action.showAll': 'Show all',

  // Conflicts.
  'conflict.collection': 'Collection',
  'conflict.deletedVersion': 'Deleted version conflicts with another change',
  'conflict.multipleChanges': 'Multiple offline changes',
  'conflict.title': 'Conflict',
  'conflict.chooseVersion': 'Choose the version to keep',
  'conflict.note': 'Both encrypted versions are kept until you choose. A deleted version can be selected deliberately.',
  'conflict.loading': 'Loading versions…',
  'conflict.deleted': 'Deleted version',
  'conflict.resolving': 'Resolving…',
  'conflict.keepDeleted': 'Keep deleted',
  'conflict.useThis': 'Use this version',
  'conflict.errOpen': 'Could not open the conflicting versions.',
  'conflict.errResolve': 'Could not resolve this conflict.',
  'conflict.screenNote': 'Changes made offline on more than one device stay encrypted and are kept until you choose which version to keep.',
  'conflict.none': 'No conflicts.',

  // Collections screen.
  'collections.newName': 'New collection name',
  'collections.note': 'Collections are private folders. An item may belong to more than one Collection.',
  'collections.confirmDelete': 'Delete the collection “{name}”? The items stay in Safe Wallet.',

  // Confirmations.
  'item.confirmDelete': 'Delete “{title}”? It can be restored from the backup history for a limited time.',
  'reminder.confirmDone': 'Mark this reminder as completed?\n\nThe reminder date stays on the item.',
  'reset.confirmOther': 'Remove “{name}” from this browser? Its encrypted backup in the account is not deleted, and your other wallets on this browser are left untouched. You will need the backup account and PIN to add it here again.',
  'reset.confirmLast': 'Remove this local wallet from this browser? Encrypted data that has been synced is not deleted from the account. Changes still waiting to upload, and this browser’s device approval, will be cleared.',

  // Merging a local wallet into an account wallet.
  'merge.confirmAdd': 'This account already has a wallet. Add your {items}, including photos and files, to it and switch to it?\n\nYour current wallet is kept on this browser until you remove it.',
  'merge.notice': 'Added {items}, including their photos and files, to this wallet. Your old wallet “{name}” is still on this browser. Once its backup finishes uploading you can remove it below.',
  'merge.removeOld': 'Remove old wallet',
  'merge.backingUp': 'Backing up…',
  'merge.dismiss': 'Dismiss',
  'merge.confirmRemoveOld': 'Remove the old wallet “{name}” from this browser?\n\nIts items are already copied into this wallet and backed up to your account. This only clears the old local copy on this browser.',
  'merge.errMismatch': 'The wallet that opened does not match the account chosen for the copy. Nothing was removed from the original wallet.',
  'merge.errOpenLocal': 'Open the local wallet before adding its items to another wallet.',
  'merge.errNoWallet': 'This account does not have a wallet to add items to.',
  'merge.errKeyPackage': 'Open the account’s wallet once on the device where it already works, then try again.',
  'merge.errPin': 'That PIN does not open the wallet already on this account. Enter that wallet’s PIN.',

  // Results of changing the PIN.
  'pin.errNoSync': 'Sync is not configured, so the PIN cannot be updated for new devices.',
  'pin.errNoEmail': 'This wallet has no account email on this device.',
  'pin.errOffline': 'You appear to be offline. Reconnect to change the PIN for signing in on new devices.',
  'pin.errWrongPassword': 'That account password is not correct.',
  'pin.errDeviceUnavailable': 'This device is not available.',
  'pin.changedBoth': 'PIN changed on this device and for signing in on new devices.',
  'pin.changedLocal': 'App PIN changed. Use the new PIN the next time this device locks.',

  // Resetting the sync account password. Kept sharply distinct from the app PIN, because
  // confusing the two is the most damaging misunderstanding a user can have here.
  'recover.accountTitle': 'Safe Wallet account',
  'recover.passwordChanged': 'Password changed',
  'recover.passwordChangedNote': 'Sign in again with the new account password. Your app PIN and your encrypted wallet were not changed.',
  'recover.continue': 'Continue',
  'recover.choosePassword': 'Choose a new password',
  'recover.choosePasswordNote': 'This changes the sync account password only. It does not change your app PIN, and on its own it cannot open your encrypted wallet on a new device.',
  'recover.checkingLink': 'Checking the recovery link…',
  'recover.newPassword': 'New account password',
  'recover.repeatPassword': 'Repeat new password',
  'recover.updating': 'Updating…',
  'recover.updatePassword': 'Update password',
  'recover.errShort': 'Use at least 8 characters.',
  'recover.errMismatch': 'The two password entries do not match.',
  'recover.errUpdate': 'Could not update the account password.',

  // Recovering a wallet when the PIN is lost. The warnings here are anti-fraud wording:
  // they must stay blunt in every language.
  'recover.walletTitle': 'Wallet recovery',
  'recover.freshVerification': 'Recovering a wallet always needs a fresh check of your account. Safe Wallet never asks a trusted contact for your code.',
  'recover.useOnlyWhen': 'Use this only when no device of your own can reset your PIN. You need a fresh email check and approval from one trusted Safe Wallet contact.',
  'recover.accountEmail': 'Account email',
  'recover.emailCode': 'Email verification code',
  'recover.sendAnother': 'Send another code',
  'recover.authenticator': 'Authenticator',
  'recover.authenticatorCode': 'Authenticator code',
  'recover.verifyStart': 'Check email and start',
  'recover.verifyContinue': 'Check email and continue',
  'recover.deviceFingerprint': 'Fingerprint of the device recovering',
  'recover.readFingerprint': 'Read this fingerprint to your trusted contact on a live call. They should call you on contact details they already had — never on a number sent to them for this.',
  'recover.status': 'Status:',
  'recover.waitingEnds': 'Waiting period ends:',
  'recover.expires': 'Expires:',
  'recover.cancel': 'Cancel recovery',
  'recover.setNewPin': 'Set a new app PIN',
  'recover.keyRebuilt': 'Your wallet key was rebuilt only in this browser. Recovery protection is used up now and has to be set up again.',
  'recover.deviceName': 'Device name',
  'recover.newPin': 'New app PIN',
  'recover.repeatPin': 'Repeat PIN',

  // Being asked to act as someone's trusted contact.
  'invite.securityConfirmation': 'Security confirmation',
  'invite.title': 'Recovery invitation',
  'invite.helpSomeone': 'Help someone you trust',
  'invite.invitedBy': 'Invited by',
  'invite.askedYou': 'They have asked you to be a trusted recovery contact.',

  // What a helper is promised when accepting. These must stay accurate: they are the
  // reassurance someone relies on when agreeing to take part.
  'invite.cannotSee': 'You cannot see their wallet.',
  'invite.cannotSeeNote': 'What they have saved stays encrypted and private.',
  'invite.cannotRecover': 'You cannot recover it on your own.',
  'invite.cannotRecoverNote': 'You only help after the owner has started recovery themselves.',
  'invite.nothingToRemember': 'Nothing for you to remember.',
  'invite.nothingToRememberNote': 'Safe Wallet prepares everything safely once you accept.',
  'invite.enterPin': 'Enter your PIN',
  'invite.confirming': 'Confirming…',
  'invite.decline': 'Decline',
  'invite.accept': 'Accept invitation',
  'invite.confirmWithPin': 'Confirm with your PIN',
  'invite.acceptTitle': 'Accept recovery invitation',
  'invite.confirmBecome': 'Confirm that you want to become a recovery contact for:',
  'invite.declineTitle': 'Decline invitation',
  'invite.declineQuestion': 'Not become a recovery contact?',
  'invite.invitationFrom': 'The invitation from',
  'invite.keep': 'Keep invitation',
  'invite.accepted': 'Invitation accepted',
  'invite.nowContact': 'You are now a recovery contact',
  'invite.nothingUnless': 'There is nothing for you to do unless',
  'invite.inboxTitle': 'Recovery invitations',
  'invite.review': 'Review invitation',
  'invite.errConfirm': 'Could not confirm this.',
  'invite.errAccept': 'Could not accept this invitation.',
  'invite.errDecline': 'Could not decline this invitation.',

  // Setting up your own recovery protection.
  'protect.preparing': 'Preparing recovery protection…',
  'protect.addPerson': 'Add a trusted person',
  'protect.addPersonNote': 'A trusted person can help you get back into your wallet. They cannot see your wallet, and they cannot recover it without you.',
  'protect.addSoYouCan': 'Add a trusted person so you can get back in if you lose your PIN or your device.',
  'protect.mustHaveAccount': 'They must already have a Safe Wallet account.',
  'protect.useTheirEmail': 'Use the email address connected to their Safe Wallet.',
  'protect.emailLabel': 'Trusted person’s Safe Wallet email',
  'protect.emailPlaceholder': 'name@example.com',
  'protect.send': 'Send invitation',
  'protect.yourPeople': 'Your trusted people',
  'protect.onlyYouSee': 'Only you can see this list.',
  'protect.waiting': 'Waiting for them to accept',
  'protect.pending': 'Pending',
  'protect.canHelp': 'Can help when you ask for recovery',
  'protect.active': 'Active',
  'protect.protectionActive': 'Recovery protection is on',
  'protect.becomesActive': 'Recovery protection starts working once one invited person accepts.',
  'protect.errLoad': 'Could not load recovery protection.',
  'protect.errSelf': 'Choose a different Safe Wallet user.',
  'protect.errNoAccount': 'No Safe Wallet account was found for this email. Ask this person to create and confirm their account first.',
  'protect.errNotReady': 'This Safe Wallet account is not ready for recovery invitations yet. Ask this person to open and unlock their updated Safe Wallet once, then try again.',
  'protect.errCheck': 'Could not check this Safe Wallet account.',
  'protect.errSend': 'Could not send this invitation.',
  'invite.willBeRemoved': 'will be removed. They can invite you again later.',
  'invite.asksForHelp': 'asks for help getting back into their wallet.',
  'protect.notSetUp': 'Not set up',
  'protect.checking': 'Checking…',
  'reminder.done': 'Done',
  'reminder.doneOn': 'Done {date}',
  'invite.enterWalletPin': 'Enter your Safe Wallet PIN',
  'invite.confirmInviteNote': 'Confirm that you want to invite this person as a recovery contact.',
  'invite.confirmInvite': 'Confirm invitation',
  'recover.startTitle': 'Start wallet recovery',
  'recover.continueTitle': 'Continue wallet recovery',
  'invite.invitedYouTo': 'invited you to be a trusted recovery contact.',
  'approve.inboxTitle': 'Someone needs your help',
  'approve.isRecovering': 'is trying to get back into their wallet.',
  'approve.review': 'Review request',
  'approve.title': 'Approve getting back in?',
  'approve.fingerprintLabel': 'Code shown on their new device',
  'approve.callFirst': 'Phone them first — on a number you already had, never one you were sent. Ask them to read out the code on their screen and check it matches this one exactly.',
  'approve.onlyIfSure': 'Only approve if the code matches and you are sure it is really them. If it is not, tell them straight away so they can cancel it.',
  'approve.approve': 'Codes match — approve',
  'approve.approving': 'Approving…',
  'approve.notNow': 'Not now',
  'approve.done': 'Approved. They can get back in now.',
  'approve.errApprove': 'Could not approve this request.',
  'approve.waitUntil': 'They can continue from {time}.',
} as const;

// Counted phrases. Each language supplies only the forms its grammar uses; `other` is
// required and acts as the fallback. Slovenian needs one/two/few/other, Romanian one/few/other.
export const plurals = {
  items: { one: '{count} item', other: '{count} items' },
  minutes: { one: '{count} minute', other: '{count} minutes' },
  queued: { one: '{count} queued change', other: '{count} queued changes' },
  helpers: { one: '{count} trusted person ready to help.', other: '{count} trusted people ready to help.' },
} as const;
